// URL-Autolinking für Klartext-Anzeigen (Kommentare/Notizen, Gäste-Chat).
// Zerlegt den Text in Klartext- und <a>-Segmente und rendert echte Elemente —
// kein dangerouslySetInnerHTML, daher kein HTML-Injection-Risiko. Verwendet
// dieselbe URL-Regex + Verkürzung wie der RichTextEditor.

import type { JSX } from "preact";

/** Matches http/https URLs; trailing punctuation is excluded from the match. */
export const URL_RE = /(https?:\/\/[^\s<]+[^\s<.,;:!?)"'\]])/gi;

/** Shortens a URL for display: drops protocol/www, truncates long paths. */
export function shortenUrl(raw: string, max = 48): string {
  let display = raw;
  try {
    const u = new URL(raw);
    const host = u.hostname.replace(/^www\./, "");
    const path = u.pathname === "/" ? "" : u.pathname;
    display = host + path + (u.search ? "?…" : "");
  } catch {
    display = raw.replace(/^https?:\/\/(www\.)?/, "");
  }
  if (display.length > max) display = display.slice(0, max - 1) + "…";
  return display;
}

/**
 * Renders plain text with clickable links. Only http/https URLs are turned into
 * <a> elements (so the href scheme is always safe); everything else stays
 * literal text. Newlines are preserved by the caller's `whitespace-pre-wrap`
 * container.
 */
export function LinkifiedText({ text }: { text: string }): JSX.Element {
  const parts: (string | JSX.Element)[] = [];
  const re = new RegExp(URL_RE.source, "gi");
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const url = m[0];
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <a
        key={key++}
        href={url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        class="text-[color:var(--color-primary)] underline underline-offset-2 hover:text-[color:var(--color-primary-strong)] break-all"
      >
        {shortenUrl(url)}
      </a>,
    );
    last = m.index + url.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}
